import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Heart, Home as HomeIcon, Loader2, ShieldCheck, Search as SearchIcon,
  Bell, MapPin, SlidersHorizontal,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import AppHeader from '../components/AppHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import { PLACE_TYPE_LABELS } from '../lib/listingConstants';

const API = process.env.REACT_APP_BACKEND_URL;

const ListingCard = ({ listing, onToggleSave }) => {
  const photo = (listing.photos || [])[0];
  const src = photo?.signed_token ? `${API}/api/files/${photo.signed_token}` : null;
  return (
    <Link
      to={`/listings/${listing.listing_id}`}
      className="group block rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-soft transition-shadow"
      data-testid={`listing-card-${listing.listing_id}`}
    >
      <div className="relative aspect-[4/3] bg-slate-100">
        {src ? (
          <img src={src} alt={listing.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
        ) : (
          <div className="h-full flex items-center justify-center"><HomeIcon className="h-10 w-10 text-slate-300" /></div>
        )}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); onToggleSave(listing); }}
          className={`absolute top-2 right-2 h-9 w-9 rounded-full bg-white/90 shadow-sm flex items-center justify-center ${
            listing.saved_by_me ? 'text-rose-600' : 'text-slate-700'
          }`}
          data-testid={`card-save-${listing.listing_id}`}
        >
          <Heart className={`h-4 w-4 ${listing.saved_by_me ? 'fill-current' : ''}`} />
        </button>
        <div className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-0.5 bg-white/90 rounded-full text-[10px] font-bold">
          <ShieldCheck className="h-3 w-3 text-emerald-700" /> Verified
        </div>
      </div>
      <div className="p-3 space-y-1">
        <h3 className="font-heading font-semibold text-sm text-slate-900 line-clamp-1">{listing.title || 'Untitled home'}</h3>
        <p className="text-xs text-slate-500 line-clamp-1">
          {PLACE_TYPE_LABELS[listing.place_type] || ''}
          {listing.neighborhood ? ` · ${listing.neighborhood}` : ''}
          {listing.city ? `, ${listing.city}` : ''}
        </p>
        <div className="pt-1 flex items-end justify-between">
          <div>
            <span className="text-lg font-heading font-extrabold text-slate-900">${(listing.total_monthly_cost_cad || 0).toLocaleString()}</span>
            <span className="text-xs text-slate-500">/mo</span>
            <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 bg-amber-500 text-white rounded-full">ALL-IN</span>
          </div>
          {listing.lease_length_months && (
            <span className="text-[11px] text-slate-500">{listing.lease_length_months} mo</span>
          )}
        </div>
      </div>
    </Link>
  );
};

const SearchResults = () => {
  const { api } = useAuth();
  const { bundle } = useProfile();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [q, setQ] = useState(params.get('q') || '');
  const [maxPrice, setMaxPrice] = useState(params.get('max_price') || bundle?.user?.monthly_budget_cad || '');
  const [placeType, setPlaceType] = useState(params.get('place_types') || '');
  const [moveInBy, setMoveInBy] = useState(params.get('move_in_by') || '');
  const [sort, setSort] = useState(params.get('sort') || 'newest');

  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [savingAlert, setSavingAlert] = useState(false);

  const load = async (pageToLoad = 1, append = false) => {
    setLoading(true);
    const sp = new URLSearchParams();
    if (q) sp.set('q', q);
    if (maxPrice) sp.set('max_price', String(maxPrice));
    if (placeType) sp.set('place_types', placeType);
    if (moveInBy) sp.set('move_in_by', moveInBy);
    sp.set('sort', sort);
    sp.set('page', String(pageToLoad));
    if (bundle?.user?.school_lat && bundle?.user?.school_lng) {
      sp.set('near_lat', String(bundle.user.school_lat));
      sp.set('near_lng', String(bundle.user.school_lng));
    }
    try {
      const res = await api.get(`/listings/search?${sp.toString()}`);
      setListings(append ? [...listings, ...res.data.listings] : res.data.listings);
      setTotal(res.data.total);
      setHasMore(res.data.has_more);
      setPage(pageToLoad);
    } catch (e) {
      toast.error("We couldn't load listings right now.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  const submitFilters = (e) => {
    e?.preventDefault?.();
    const sp = new URLSearchParams();
    if (q) sp.set('q', q);
    if (maxPrice) sp.set('max_price', String(maxPrice));
    if (placeType) sp.set('place_types', placeType);
    if (moveInBy) sp.set('move_in_by', moveInBy);
    sp.set('sort', sort);
    setParams(sp);
    load(1, false);
  };

  const toggleSave = async (listing) => {
    try {
      if (listing.saved_by_me) {
        await api.delete(`/saved-listings/${listing.listing_id}`);
      } else {
        await api.post('/saved-listings', { listing_id: listing.listing_id });
      }
      setListings((ls) => ls.map((l) => l.listing_id === listing.listing_id ? { ...l, saved_by_me: !l.saved_by_me } : l));
    } catch (_) {
      toast.error("Couldn't update your saved list.");
    }
  };

  const createAlert = async () => {
    setSavingAlert(true);
    try {
      await api.post('/alerts', {
        email: bundle?.user?.email || '',
        school_name: bundle?.user?.school_name || null,
        school_place_id: bundle?.user?.school_place_id || null,
        school_lat: bundle?.user?.school_lat || null,
        school_lng: bundle?.user?.school_lng || null,
        monthly_budget_cad: maxPrice ? Number(maxPrice) : (bundle?.user?.monthly_budget_cad || null),
        source: 'search_empty_state',
      });
      toast.success("You're on the list. We'll email you when a match goes live.");
    } catch (_) {
      toast.error("Couldn't save that alert.");
    } finally {
      setSavingAlert(false);
    }
  };

  const resetFilters = () => {
    setQ(bundle?.user?.school_name || '');
    setMaxPrice(bundle?.user?.monthly_budget_cad || '');
    setPlaceType('');
    setMoveInBy('');
    setSort('newest');
    setParams(new URLSearchParams());
    load(1, false);
  };

  return (
    <div className="min-h-screen bg-background pb-12" data-testid="search-results-page">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-8 space-y-5">
        {/* Search bar */}
        <form onSubmit={submitFilters} className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search school, city, or neighborhood"
              className="h-12 pl-10"
              data-testid="search-input"
            />
          </div>
          <Button type="submit" className="h-12 px-6 btn-primary" data-testid="search-submit">
            Search
          </Button>
        </form>

        {/* Filter pills */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-2 overflow-x-auto scrollbar-thin">
            <button
              type="button"
              onClick={() => { setMaxPrice(900); submitFilters(); }}
              className="flex-shrink-0 px-3 py-1.5 rounded-full border border-slate-200 bg-white text-xs font-medium hover:border-amber-300"
              data-testid="pill-under-900"
            >
              Under $900
            </button>
            <button
              type="button"
              onClick={() => { setPlaceType('private_room'); submitFilters(); }}
              className="flex-shrink-0 px-3 py-1.5 rounded-full border border-slate-200 bg-white text-xs font-medium hover:border-amber-300"
              data-testid="pill-private-room"
            >
              Private room
            </button>
            <button
              type="button"
              onClick={() => { setPlaceType('basement_apartment'); submitFilters(); }}
              className="flex-shrink-0 px-3 py-1.5 rounded-full border border-slate-200 bg-white text-xs font-medium hover:border-amber-300"
              data-testid="pill-basement"
            >
              Basement
            </button>
            <button
              type="button"
              onClick={() => { setPlaceType('one_bedroom'); submitFilters(); }}
              className="flex-shrink-0 px-3 py-1.5 rounded-full border border-slate-200 bg-white text-xs font-medium hover:border-amber-300"
              data-testid="pill-one-bed"
            >
              1 bedroom
            </button>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-9 w-[170px] text-xs" data-testid="sort-select">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="closest">Closest to school</SelectItem>
                <SelectItem value="price_asc">Price: low to high</SelectItem>
                <SelectItem value="price_desc">Price: high to low</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="most_saved">Most saved</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
          </div>
        ) : listings.length === 0 ? (
          <Card className="bg-card-cream border-amber-200/60">
            <CardContent className="p-8 sm:p-10 text-center">
              <div className="mx-auto w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mb-4">
                <HomeIcon className="h-7 w-7 text-amber-600" />
              </div>
              <h2 className="text-xl font-heading font-extrabold text-slate-900">
                No homes match your filters yet.
              </h2>
              <p className="mt-2 text-sm text-slate-700 max-w-md mx-auto">
                Try widening your search or set up an alert and we'll email you the moment a match goes live.
              </p>
              <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
                <Button variant="outline" onClick={resetFilters} data-testid="reset-filters-btn">
                  Reset filters
                </Button>
                <Button className="btn-primary" onClick={createAlert} disabled={savingAlert} data-testid="create-alert-btn">
                  {savingAlert && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                  <Bell className="h-4 w-4 mr-1.5" /> Create an alert
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <p className="text-xs text-slate-500" data-testid="results-count">
              Showing {listings.length} of {total} home{total === 1 ? '' : 's'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="results-grid">
              {listings.map((l) => (
                <ListingCard key={l.listing_id} listing={l} onToggleSave={toggleSave} />
              ))}
            </div>
            {hasMore && (
              <div className="flex justify-center pt-2">
                <Button
                  variant="outline"
                  onClick={() => load(page + 1, true)}
                  data-testid="load-more-btn"
                >
                  Load more
                </Button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default SearchResults;
